import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { CalendarDays, Plus, Clock, CheckCircle, XCircle, AlertCircle, Filter, X, MessageSquare } from 'lucide-react';
import { canUserEdit } from '@/utils/permissions';

function Leave() {
  const { API, user } = useContext(AppContext);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [actionRemarks, setActionRemarks] = useState('');
  const [formData, setFormData] = useState({
    leaveType: 'Casual Leave',
    fromDate: '',
    toDate: '',
    reason: '',
    contactDuringLeave: ''
  });

  const leaveTypes = ['Casual Leave', 'Sick Leave', 'Earned Leave', 'Maternity Leave', 'Paternity Leave', 'Comp Off', 'LOP'];
  
  // Check if current user can manage leaves
  const isAdminOrHR = canUserEdit(user, '/hr/leave');

  useEffect(() => {
    fetchLeaves();
  }, [statusFilter]);

  const fetchLeaves = async () => {
    try {
      let url = `${API}/hr/leaves?status=${statusFilter}`;
      // If not admin/HR, only show own leaves
      if (!isAdminOrHR && user?.id) {
        url += `&employeeId=${user.id}`;
      }
      const response = await axios.get(url);
      setLeaves(response.data);
    } catch (error) {
      console.error('Error fetching leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        employeeId: user?.id || 'EMP-001',
        employeeName: user?.name || 'Current User',
        ...formData
      };
      await axios.post(`${API}/hr/leaves`, payload);
      setShowApplyModal(false);
      setFormData({ leaveType: 'Casual Leave', fromDate: '', toDate: '', reason: '', contactDuringLeave: '' });
      fetchLeaves();
    } catch (error) {
      console.error('Error applying leave:', error);
      alert('Failed to apply leave');
    }
  };

  const handleLeaveAction = async (status) => {
    try {
      const payload = {
        status,
        remarks: actionRemarks,
      };
      await axios.put(`${API}/hr/leaves/${selectedLeave.id}`, payload);
      setShowActionModal(false);
      setSelectedLeave(null);
      setActionRemarks('');
      fetchLeaves();
    } catch (error) {
      console.error('Error updating leave:', error);
      alert('Failed to update leave status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved': return <CheckCircle className="text-green-500" size={18} />;
      case 'Rejected': return <XCircle className="text-red-500" size={18} />;
      case 'Pending': return <Clock className="text-yellow-500" size={18} />;
      default: return <AlertCircle className="text-gray-500" size={18} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const calculateDays = (from, to) => {
    if (!from || !to) return 0;
    const diffTime = Math.abs(new Date(to) - new Date(from));
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  // Summary stats
  const pendingCount = leaves.filter(l => l.status === 'Pending').length;
  const approvedCount = leaves.filter(l => l.status === 'Approved').length;
  const rejectedCount = leaves.filter(l => l.status === 'Rejected').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="text-[#1a237e]" size={28} />
            Leave Management
          </h1>
          <p className="text-gray-600 mt-1">
            {isAdminOrHR ? 'Review and manage employee leave applications' : 'Apply and track your leave requests'}
          </p>
        </div>
        <button
          onClick={() => setShowApplyModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] transition"
          data-testid="apply-leave-btn"
        >
          <Plus size={18} />
          Apply Leave
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Applications</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{leaves.length}</p>
            </div>
            <div className="h-12 w-12 bg-[#1a237e]/10 rounded-full flex items-center justify-center">
              <CalendarDays className="text-[#1a237e]" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{pendingCount}</p>
            </div>
            <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Clock className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Approved</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{approvedCount}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Rejected</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{rejectedCount}</p>
            </div>
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="text-red-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <Filter size={18} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
          <span className="text-sm text-gray-600">{leaves.length} applications found</span>
        </div>
      </div>

      {/* Leave Applications List */}
      <div className="space-y-4">
        {leaves.map((leave) => (
          <div
            key={leave.id}
            className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition"
            data-testid={`leave-card-${leave.id}`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-gradient-to-br from-[#1a237e] to-[#3949ab] rounded-full flex items-center justify-center text-white font-bold">
                  {leave.employeeName?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{leave.employeeName}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(leave.status)}`}>
                      {leave.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{leave.department} | {leave.employeeId}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg font-medium">
                      {leave.leaveType}
                    </span>
                    <span className="text-gray-600">
                      {leave.fromDate} to {leave.toDate}
                    </span>
                    <span className="text-[#e65100] font-medium">
                      {calculateDays(leave.fromDate, leave.toDate)} day(s)
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  Applied: {leave.applicationDate}
                </span>
                {isAdminOrHR && leave.status === 'Pending' && (
                  <button
                    onClick={() => { setSelectedLeave(leave); setShowActionModal(true); }}
                    className="px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] text-sm font-medium"
                    data-testid={`review-leave-${leave.id}`}
                  >
                    Review
                  </button>
                )}
              </div>
            </div>

            {/* Reason */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Reason:</span> {leave.reason}
              </p>
              {leave.remarks && (
                <p className="text-sm text-gray-500 mt-2">
                  <span className="font-medium">Remarks:</span> {leave.remarks}
                </p>
              )}
              {leave.approvedBy && (
                <p className="text-sm text-green-600 mt-1">
                  {leave.status === 'Approved' ? 'Approved' : 'Reviewed'} by: {leave.approvedBy} on {leave.approvalDate}
                </p>
              )}
            </div>
          </div>
        ))}

        {leaves.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <CalendarDays className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-500">No leave applications found</p>
          </div>
        )}
      </div>

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Apply for Leave</h2>
              <button onClick={() => setShowApplyModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleApplyLeave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
                <select
                  required
                  value={formData.leaveType}
                  onChange={(e) => setFormData({...formData, leaveType: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                  data-testid="leave-type-select"
                >
                  {leaveTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.fromDate}
                    onChange={(e) => setFormData({...formData, fromDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                    data-testid="from-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.toDate}
                    onChange={(e) => setFormData({...formData, toDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                    data-testid="to-date-input"
                  />
                </div>
              </div>
              {formData.fromDate && formData.toDate && (
                <div className="text-sm text-[#e65100] font-medium">
                  Duration: {calculateDays(formData.fromDate, formData.toDate)} day(s)
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <textarea
                  required
                  rows={3}
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none resize-none"
                  placeholder="Please provide the reason for leave..."
                  data-testid="reason-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact During Leave</label>
                <input
                  type="text"
                  value={formData.contactDuringLeave}
                  onChange={(e) => setFormData({...formData, contactDuringLeave: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                  placeholder="Phone number or email"
                  data-testid="contact-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowApplyModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593]"
                  data-testid="submit-leave-btn"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Action Modal for Admin/HR */}
      {showActionModal && selectedLeave && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Review Leave Application</h2>
              <button onClick={() => { setShowActionModal(false); setSelectedLeave(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Employee:</span>
                    <p className="font-medium">{selectedLeave.employeeName}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Department:</span>
                    <p className="font-medium">{selectedLeave.department}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Leave Type:</span>
                    <p className="font-medium">{selectedLeave.leaveType}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Duration:</span>
                    <p className="font-medium">{calculateDays(selectedLeave.fromDate, selectedLeave.toDate)} day(s)</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Period:</span>
                    <p className="font-medium">{selectedLeave.fromDate} to {selectedLeave.toDate}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Reason:</span>
                    <p className="font-medium">{selectedLeave.reason}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MessageSquare size={14} className="inline mr-1" />
                  Remarks (optional)
                </label>
                <textarea
                  rows={2}
                  value={actionRemarks}
                  onChange={(e) => setActionRemarks(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none resize-none"
                  placeholder="Add any remarks..."
                  data-testid="action-remarks-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => handleLeaveAction('Rejected')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                  data-testid="reject-leave-btn"
                >
                  <XCircle size={18} />
                  Reject
                </button>
                <button
                  onClick={() => handleLeaveAction('Approved')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  data-testid="approve-leave-btn"
                >
                  <CheckCircle size={18} />
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Leave;
